/* eslint-disable no-undef */
import * as XLSX from "xlsx";
import { useMutation } from "react-query";
import * as FileSaver from "file-saver";
import { Button } from "react-bootstrap";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DownloadIcon } from "@components/icons";
import { fetchDownloadSensorData, fetchDownloadTreeData } from "@modules/Trees/services/treeCrud";
import {
  scaleMoistureContentFields,
  getMoistureContentDisplayMode,
  getMoistureContentCapEnabled,
} from "@helper/moistureContentDisplay";

const sanitizeExcelSheetName = (name, fallback = "Sheet") => {
  const cleaned = String(name || fallback)
    .replace(/[:\\/?*[\]]/g, "_")
    .trim();

  return (cleaned || fallback).slice(0, 31);
};

const usefetchCheckState = ({ parameters }) => {
  const checkedParameters = parameters.filter((parameter) => parameter.checked && parameter.active);
  const backendNames = checkedParameters.map((parameter) => parameter.name);
  const exportNames = [...backendNames];

  const hasMoistureContent = backendNames.includes("moisture_content");

  if (hasMoistureContent) {
    ["formula_type", "calibration_species"].forEach((metadataKey) => {
      if (!exportNames.includes(metadataKey)) {
        exportNames.push(metadataKey);
      }
    });
  }
  const parameterQuery = backendNames.join(",");
  return { parameterQuery: parameterQuery, checkedNames: exportNames };
};
export const ExportData = ({
  tree,
  sensors,
  fileName,
  setSuccessMessage,
  parameters,
  includeMeanValues = true,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation();
  const authUser = useSelector((state) => state.auth.user);
  const moistureContentDisplayMode = getMoistureContentDisplayMode(authUser);
  const moistureContentCapEnabled = getMoistureContentCapEnabled(authUser);
  const { parameterQuery, checkedNames } = usefetchCheckState({
    parameters,
  });
  const sensor_ids = sensors.map((sensor) => sensor.id);

  const { mutateAsync: fetchExport, isLoading } = useMutation(() =>
    tree
      ? fetchDownloadTreeData({ tree, sensors, parameterQuery, startDate, endDate })
      : fetchDownloadSensorData({
          sensors: sensor_ids,
          parameterQuery,
          startDate,
          endDate,
        })
  );

  const resolvedFileName = fileName ? fileName : "Unknown";

  const handleDownload = async () => {
    try {
      const res = await fetchExport();
      let { data, mean } = res;
      if (tree) {
        data = data?.[tree.id] || {};
        mean = mean?.[tree.id] || {};
      } else {
        data = data || {};
        mean = mean || {};
      }
      const nextPlotData = [];
      sensors.forEach((sensor) => {
        const key = sensor.id;
        const newData = { name: sensor.name, data: [] };
        const sensorData = data?.[key];
        const sensorMean = mean?.[key];
        if (
          sensorData &&
          sensorMean &&
          Array.isArray(sensorData.time) &&
          sensorData.time.length > 0
        ) {
          sensorData.time.forEach((tim, index) => {
            let dataObj = {
              time: tim,
            };
            checkedNames.forEach((parameter) => {
              const value = sensorData?.[parameter]?.[index];
              dataObj[parameter] = value !== undefined ? value : "";
            });
            if (includeMeanValues) {
              dataObj["time_mean"] = sensorMean.time[index];
              const nonMeanFields = ["formula_type", "calibration_species"];
              checkedNames.forEach((parameter) => {
                if (nonMeanFields.includes(parameter)) return;
                const meanValue = sensorMean?.[parameter]?.[index];
                dataObj[parameter + "_mean"] = meanValue !== undefined ? meanValue : "";
              });
            }
            newData.data.push(
              scaleMoistureContentFields(
                dataObj,
                moistureContentDisplayMode,
                moistureContentCapEnabled
              )
            );
          });
          if (newData.data.length > 0) {
            nextPlotData.push(newData);
          }
        }
      });
      if (nextPlotData.length > 0) {
        exportToExcel(nextPlotData, resolvedFileName);
        setSuccessMessage(
          t("file_download_success", {
            defaultValue: "File has been downloaded successfully!",
          })
        );
      } else {
        setSuccessMessage(
          t("no_data_for_selected_range", {
            defaultValue: "No data available for the selected time range and parameters.",
          })
        );
      }
    } catch (error) {
      console.error("Export failed:", error);
      setSuccessMessage(
        t("export_failed", {
          defaultValue: "Export failed. Please try again.",
        })
      );
    }
  };

  return (
    <div className="d-flex align-items-center gap-2">
      <Button
        variant="outline-primary d-flex align-items-center"
        onClick={handleDownload}
        disabled={isLoading}
      >
        <>
          <DownloadIcon />
          {t("download")} &nbsp;
          {isLoading && <Spinner animation="border" size="sm" />}
        </>
      </Button>
    </div>
  );
};

const exportToExcel = (plotData, fileName) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";
  var wb = XLSX.utils.book_new();
  plotData.forEach((data) => {
    var wsPersonDetails = XLSX.utils.json_to_sheet(data.data);
    XLSX.utils.book_append_sheet(wb, wsPersonDetails, sanitizeExcelSheetName(data.name));
  });
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, fileName + fileExtension);
};
